// src/components/UserRolesModal.js
import React, { useState, useEffect } from 'react';
import { X, Plus } from 'react-feather';
import Modal from './Modal';
import Select from './Select';
import Button from './Buttons';
import { useApi } from '../hooks/useApi';
import {
  fetchRoleOptions,
  fetchScopeOptions,
  fetchOrgHierarchy,
  fetchUserRoles,
  updateUserRoles,
  fetchUserOrganizations,
  fetchUserRolesTenant,
  updateUserRolesTenant,
  fetchTenantRoleOptions,
  fetchTenantScopeOptions,
  fetchUserOrganizationsTenant,
  fetchTenantOrganizations,
  ORG_INVITE_ROLES,
  detectHigherLevelRole,
} from '../services/userRolesService';
import { useRBAC } from '../hooks/useRBAC';

const roleColors = {
  OWNER: 'bg-red-100 text-red-800',
  ADMIN: 'bg-purple-100 text-purple-800',
  RISK_MANAGER: 'bg-orange-100 text-orange-800',
  CONTRIBUTOR: 'bg-blue-100 text-blue-800',
  APPROVER: 'bg-green-100 text-green-800',
  VIEWER: 'bg-gray-100 text-gray-800',
};

// NOTE: at DEAL / BUSINESS_UNIT scope the role decides BREADTH, not just strength (backend
// RoleSemantics). Contributor and Approver are assignment-scoped there: they see only their
// own assigned work, which is LESS than Viewer sees, despite the stronger role value.
const permissionDescriptions = {
  OWNER: {
    TENANT: 'Full control over the entire tenant, including all organizations, users, and settings.',
    ORG: 'Full control over the organization, including settings, users, and all data.',
    BUSINESS_UNIT: 'Full control over a specific business unit and its assessments.',
    DEAL: 'Full control over a specific deal and its assessments.',
    SELF: 'Full control over assessments and risks they create.',
  },
  ADMIN: {
    TENANT: 'Can manage users, roles, organizations, and settings across the entire tenant.',
    ORG: 'Can manage users, roles, and settings for the entire organization.',
    BUSINESS_UNIT: 'Can manage users, roles, and settings for a specific business unit.',
    DEAL: 'Can manage settings and assignments for a specific deal.',
    SELF: 'Can manage settings for assessments and risks they create.',
  },
  RISK_MANAGER: {
    TENANT: 'Can create, edit, and manage risks and assessments across all organizations in the tenant.',
    ORG: 'Can create, edit, and manage risks and assessments across the organization.',
    BUSINESS_UNIT: 'Can create, edit, and manage risks and assessments for a specific business unit.',
    DEAL: 'Can create, edit, and manage risks and assessments for a specific deal.',
    SELF: 'Can manage risks and assessments they create.',
  },
  CONTRIBUTOR: {
    TENANT: 'Can fill in and submit assessments across all organizations in the tenant.',
    ORG: 'Can fill in and submit assessments across the organization.',
    BUSINESS_UNIT: 'Can fill in and submit only the assessments and risks assigned to them within a specific business unit. Cannot see the rest of it.',
    DEAL: 'Can fill in and submit only the assessments and risks assigned to them within a specific deal. Cannot see the rest of it.',
    SELF: 'Can fill in and submit assessments assigned to them.',
  },
  APPROVER: {
    TENANT: 'Can approve or reject risks across all organizations in the tenant.',
    ORG: 'Can approve or reject risks across the organization.',
    BUSINESS_UNIT: 'Can approve or reject only the risks they are named on within a specific business unit. Cannot see the rest of it.',
    DEAL: 'Can approve or reject only the risks they are named on within a specific deal. Cannot see the rest of it.',
    SELF: 'Can approve or reject risks assigned to them.',
  },
  VIEWER: {
    TENANT: 'Read-only access to all data across all organizations in the tenant.',
    ORG: 'Read-only access to all data in the organization.',
    BUSINESS_UNIT: 'Read-only access to data in a specific business unit.',
    DEAL: 'Read-only access to data in a specific deal.',
    SELF: 'Read-only access to assessments and risks assigned to them.',
  },
};

const UserRolesModal = ({ isOpen, onClose, user, onSaved, tenantMode = false, tenantId: tenantIdProp = null }) => {
  const apiCall = useApi();
  const { hasMinimumRoleAtOrAbove } = useRBAC();

  // Get current tenant — prefer explicit prop (multi-tenant admin), fall back to selectedOrganization
  const getCurrentTenant = () => {
    if (tenantMode && tenantIdProp) {
      // Resolve name from orgHierarchy
      try {
        const orgHierarchy = JSON.parse(localStorage.getItem('orgHierarchy') || '[]');
        const entry = orgHierarchy.find(h => h.tenantId === tenantIdProp);
        return { id: tenantIdProp, name: entry?.tenantName || `Tenant ${tenantIdProp}` };
      } catch (e) {
        return { id: tenantIdProp, name: `Tenant ${tenantIdProp}` };
      }
    }
    try {
      const selectedOrg = localStorage.getItem('selectedOrganization');
      if (selectedOrg) {
        const org = JSON.parse(selectedOrg);
        return { id: org?.tenantId, name: org?.tenantName };
      }
    } catch (e) {}
    return { id: null, name: null };
  };

  const currentTenant = getCurrentTenant();
  const currentTenantId = currentTenant.id;
  const currentTenantName = currentTenant.name;
  const canAdminTenant = currentTenantId
    ? hasMinimumRoleAtOrAbove('ADMIN', 'TENANT', currentTenantId)
    : false;

  // Get current org ID from selected organization
  const getCurrentOrgId = () => {
    try {
      const selectedOrg = localStorage.getItem('selectedOrganization');
      if (selectedOrg) {
        const org = JSON.parse(selectedOrg);
        return org?.organizationId;
      }
    } catch (e) {}
    return null;
  };
  const currentOrgId = getCurrentOrgId();

  // Data from API
  const [roleOptions, setRoleOptions] = useState([]);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [orgHierarchy, setOrgHierarchy] = useState([]);
  const [targetUserOrgs, setTargetUserOrgs] = useState([]);
  const [tenantOrganizations, setTenantOrganizations] = useState([]);

  // Current roles (working copy)
  const [roles, setRoles] = useState([]);

  // Form state for adding new role
  const [newRole, setNewRole] = useState({
    role: '',
    scope: '',
    scopeEntityId: '',
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(null);

  // Simple mode state
  const [simpleMode, setSimpleMode] = useState(true);
  const [selectedInviteRole, setSelectedInviteRole] = useState('');
  const [detectionResult, setDetectionResult] = useState(null);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadData();
    }
  }, [isOpen, user]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const fetchRoleOptsFn = tenantMode ? fetchTenantRoleOptions : fetchRoleOptions;
      const fetchRolesFn = tenantMode ? fetchUserRolesTenant : fetchUserRoles;
      const fetchScopesFn = tenantMode ? fetchTenantScopeOptions : fetchScopeOptions;
      const fetchUserOrgsFn = tenantMode ? fetchUserOrganizationsTenant : fetchUserOrganizations;
      const [rolesRes, scopesRes, hierarchyRes, userRolesRes, userOrgsRes] = await Promise.all([
        fetchRoleOptsFn(apiCall),
        fetchScopesFn(apiCall),
        fetchOrgHierarchy(apiCall),
        fetchRolesFn(apiCall, user.id),
        fetchUserOrgsFn(apiCall, user.id),
      ]);

      setRoleOptions(rolesRes || []);
      setScopeOptions(scopesRes || []);
      setOrgHierarchy(hierarchyRes || []);
      setRoles(userRolesRes || []);
      setTargetUserOrgs(userOrgsRes || []);

      // Run role detection for simple mode (org mode only)
      if (!tenantMode && currentOrgId) {
        const detection = detectHigherLevelRole(userRolesRes || [], currentOrgId);
        setDetectionResult(detection);
        setSelectedInviteRole(detection.matchedRole?.key || '');
        setSimpleMode(true);
      } else {
        setSimpleMode(false);
        setDetectionResult(null);
      }

      // In tenant mode, fetch all organizations for tenant scope entity selection
      if (tenantMode) {
        try {
          const orgs = await fetchTenantOrganizations(apiCall);
          setTenantOrganizations(orgs || []);
        } catch (err) {}
      }
    } catch (err) {
      setError('Failed to load role data');
    } finally {
      setLoading(false);
    }
  };

  // Deal/BU options for scope entity selection
  const [dealOptions, setDealOptions] = useState([]);
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);

  // Fetch deals/BUs when DEAL or BUSINESS_UNIT scope is selected
  useEffect(() => {
    const scopeInt = parseInt(newRole.scope);
    if (scopeInt === 5 && dealOptions.length === 0) {
      // Fetch deals for the current org
      apiCall('GET', '/Deals')
        .then(res => setDealOptions(res || []))
        .catch(() => {});
    }
    if (scopeInt === 4 && businessUnitOptions.length === 0) {
      // Fetch business units for the current org
      apiCall('GET', '/BusinessUnits')
        .then(res => setBusinessUnitOptions(res || []))
        .catch(() => {});
    }
  }, [newRole.scope]);

  // Get entities based on selected scope
  // For ORG scope, use the target user's organizations (orgs they are a member of)
  // For TENANT scope, use the org hierarchy (filtered to tenants the current user can manage)
  const getScopeEntities = (scopeValue) => {
    const scopeInt = parseInt(scopeValue);
    switch (scopeInt) {
      case 1: // ACCOUNT
        const accounts = [...new Map(
          orgHierarchy.map(h => [h.accountId, { id: h.accountId, name: h.accountName }])
        ).values()];
        return accounts;

      case 2: // TENANT
        if (tenantMode && currentTenantId) {
          // Tenant mode: show only the current tenant
          // Resolve name from target user's orgs (which include tenantName from the API)
          const tenantName = currentTenantName
            || targetUserOrgs.find(o => o.tenantId === currentTenantId)?.tenantName
            || `Tenant ${currentTenantId}`;
          return [{ id: currentTenantId, name: tenantName }];
        }
        const tenants = [...new Map(
          orgHierarchy
            .filter(h => h.tenantId)
            .map(h => [h.tenantId, { id: h.tenantId, name: h.tenantName || `Tenant ${h.tenantId}` }])
        ).values()];
        return tenants;

      case 3: // ORG
        if (tenantMode) {
          // Tenant mode: show all orgs in the tenant
          return tenantOrganizations.map(o => ({
            id: o.organizationId,
            name: o.organizationName,
          }));
        }
        // Org mode: filter to current org only
        return targetUserOrgs
          .filter(o => o.organizationId === currentOrgId)
          .map(o => ({
            id: o.organizationId,
            name: o.organizationName,
          }));

      case 4: // BUSINESS_UNIT
        return businessUnitOptions.map(bu => ({
          id: bu.businessUnitId,
          name: bu.businessUnitName,
        }));

      case 5: // DEAL
        return dealOptions.map(d => ({
          id: d.dealID,
          name: d.dealName,
        }));

      case 6: // SELF — uses org list (ScopeEntityId = orgId)
        return targetUserOrgs
          .filter(o => o.organizationId === currentOrgId)
          .map(o => ({
            id: o.organizationId,
            name: o.organizationName,
          }));

      default:
        return [];
    }
  };

  const handleAddRole = () => {
    if (!newRole.role || !newRole.scope || !newRole.scopeEntityId) {
      setError('Please select role, scope, and entity');
      return;
    }

    // Check for duplicates
    const isDuplicate = roles.some(r =>
      r.roleValue === parseInt(newRole.role) &&
      r.scopeValue === parseInt(newRole.scope) &&
      r.scopeEntityId === parseInt(newRole.scopeEntityId)
    );

    if (isDuplicate) {
      setError('This role assignment already exists');
      return;
    }

    // Build display object
    const roleOption = roleOptions.find(r => r.value === parseInt(newRole.role));
    const scopeOption = scopeOptions.find(s => s.value === parseInt(newRole.scope));
    const entities = getScopeEntities(newRole.scope);
    const entity = entities.find(e => e.id === parseInt(newRole.scopeEntityId));

    setRoles([...roles, {
      userRoleId: null,
      role: roleOption?.name,
      roleValue: parseInt(newRole.role),
      scope: scopeOption?.name,
      scopeValue: parseInt(newRole.scope),
      scopeEntityId: parseInt(newRole.scopeEntityId),
      scopeEntityName: entity?.name,
    }]);

    // Reset form
    setNewRole({ role: '', scope: '', scopeEntityId: '' });
    setError('');
  };

  const handleRemoveRole = (roleToRemove) => {
    setRoles(roles.filter(r => r !== roleToRemove));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const payload = roles.map(r => ({
        role: r.roleValue,
        scope: r.scopeValue,
        scopeEntityId: r.scopeEntityId,
      }));

      const saveFn = tenantMode ? updateUserRolesTenant : updateUserRoles;
      await saveFn(apiCall, user.id, payload);
      onSaved();
    } catch (err) {
      setError('Failed to save roles. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setNewRole({ role: '', scope: '', scopeEntityId: '' });
    setError('');
    setShowUnsavedWarning(null);
    setSimpleMode(true);
    setSelectedInviteRole('');
    setDetectionResult(null);
    onClose();
  };

  const handleSimpleSave = async () => {
    if (!selectedInviteRole) {
      setError('Please select a role');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const inviteRole = ORG_INVITE_ROLES.find(r => r.key === selectedInviteRole);
      if (!inviteRole) {
        setError('Invalid role selection');
        setSaving(false);
        return;
      }
      // Build payload: the selected invite role + preserved extra (DEAL/BU) roles
      const extraRoles = detectionResult?.extraRoles || [];
      const payload = [
        { role: inviteRole.role, scope: inviteRole.scope, scopeEntityId: currentOrgId },
        ...extraRoles.map(r => ({
          role: r.roleValue ?? r.role,
          scope: r.scopeValue ?? r.scope,
          scopeEntityId: r.scopeEntityId,
        })),
      ];
      await updateUserRoles(apiCall, user.id, payload);
      onSaved();
    } catch (err) {
      setError('Failed to save roles. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasPendingNewRole = newRole.role && newRole.scope && newRole.scopeEntityId;

  const handleSaveClick = () => {
    if (simpleMode && !tenantMode) {
      handleSimpleSave();
      return;
    }
    if (hasPendingNewRole) { setShowUnsavedWarning('save'); return; }
    handleSave();
  };

  const handleCloseClick = () => {
    if (hasPendingNewRole) { setShowUnsavedWarning('cancel'); return; }
    handleClose();
  };

  // Reset entity selection when scope changes; auto-select if only one option
  const handleScopeChange = (e) => {
    setError('');
    const newScope = e.target.value;
    const entities = getScopeEntities(newScope);
    setNewRole({
      ...newRole,
      scope: newScope,
      scopeEntityId: entities.length === 1 ? entities[0].id.toString() : '',
    });
  };

  const getPermissionDescription = () => {
    const roleName = roleOptions.find(r => r.value === parseInt(newRole.role))?.name;
    const scopeName = scopeOptions.find(s => s.value === parseInt(newRole.scope))?.name;
    if (!roleName || !scopeName) return null;
    return permissionDescriptions[roleName]?.[scopeName] || null;
  };

  const scopeEntities = getScopeEntities(newRole.scope);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseClick}
      title={`Edit Roles for ${user?.fullName || 'User'}`}
      size="2.5xl"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* User info */}
          <p className="text-sm text-gray-500">{user?.email}</p>

          {/* Simple mode (org mode only) */}
          {simpleMode && !tenantMode ? (
            <div>
              {/* Ambiguous/custom roles warning */}
              {detectionResult?.isAmbiguous && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-amber-800 font-medium">
                    This user has custom role assignments. If you select and save a role here, it will overwrite their custom roles. Use the advanced editor below to view current assignments.
                  </p>
                </div>
              )}

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Role</h3>
                <Select
                  value={selectedInviteRole}
                  onChange={(e) => { setError(''); setSelectedInviteRole(e.target.value); }}
                  options={[
                    { value: '', label: '-- Select Role --' },
                    ...ORG_INVITE_ROLES.map(r => ({ value: r.key, label: r.displayName })),
                  ]}
                />
              </div>

              {/* Info note about extra DEAL/BU roles */}
              {detectionResult?.extraRoles?.length > 0 && (
                <p className="text-sm text-blue-600 mt-2">
                  {detectionResult.extraRoles.length} additional role assignment{detectionResult.extraRoles.length !== 1 ? 's' : ''} will be preserved.
                </p>
              )}

              {/* Error message */}
              {error && (
                <p className="text-sm text-red-500 mt-2">{error}</p>
              )}

              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 mt-4 underline"
                onClick={() => setSimpleMode(false)}
              >
                Advanced role editing
              </button>
            </div>
          ) : (
            <div>
              {/* Back to simple link (org mode only) */}
              {!tenantMode && (
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800 mb-4 underline"
                  onClick={() => setSimpleMode(true)}
                >
                  Back to simple view
                </button>
              )}

              {/* Current roles */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Current Roles</h3>
                {roles.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No roles assigned</p>
                ) : (
                  <div className="space-y-2">
                    {roles.map((role, index) => (
                      <div
                        key={role.userRoleId || `new-${index}`}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[role.roleValue === 1 ? 'ADMIN' : role.role] || 'bg-gray-100 text-gray-800'}`}>
                            {role.roleValue === 1
                              ? 'Admin'
                              : (roleOptions.find(r => r.value === role.roleValue)?.displayName || role.role)}
                          </span>
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                            {scopeOptions.find(s => s.value === role.scopeValue)?.displayName || role.scope}
                          </span>
                          <span className="text-sm text-gray-600">
                            {role.scopeEntityName
                              || getScopeEntities(role.scopeValue?.toString())?.find(e => e.id === role.scopeEntityId)?.name
                              || `ID: ${role.scopeEntityId}`}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveRole(role)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove role"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add new role */}
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Add New Role</h3>
                <div className="grid grid-cols-4 gap-3 items-end -mb-3">
                  <Select
                    label="Role"
                    value={newRole.role}
                    onChange={(e) => { setError(''); setNewRole({ ...newRole, role: e.target.value }); }}
                    className="!py-1.5"
                    options={[
                      { value: '', label: '-- Select Role --' },
                      // OWNER (value 1) is provisioning-only — never offer it as an assignable role.
                      ...roleOptions
                        .filter(r => r.value !== 1)
                        .map(r => ({ value: r.value.toString(), label: r.displayName })),
                    ]}
                  />
                  <Select
                    label="Scope"
                    value={newRole.scope}
                    onChange={handleScopeChange}
                    className="!py-1.5"
                    options={[
                      { value: '', label: '-- Select Scope --' },
                      ...scopeOptions.map(s => ({ value: s.value.toString(), label: s.displayName })),
                    ]}
                  />
                  <Select
                    label="Entity"
                    value={newRole.scopeEntityId}
                    onChange={(e) => { setError(''); setNewRole({ ...newRole, scopeEntityId: e.target.value }); }}
                    className="!py-1.5"
                    options={[
                      { value: '', label: '-- Select Entity --' },
                      ...scopeEntities.map(e => ({ value: e.id.toString(), label: e.name })),
                    ]}
                    disabled={!newRole.scope}
                  />
                  <div className="mb-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleAddRole}
                      disabled={!newRole.role || !newRole.scope || !newRole.scopeEntityId}
                    >
                      + Add Role
                    </Button>
                  </div>
                </div>
                {/* Error message */}
                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}
                {/* Permission description */}
                {getPermissionDescription() && (
                  <p className="mt-1 text-base text-blue-500 italic">
                    {getPermissionDescription()}
                  </p>
                )}
              </div>

              {/* Unsaved role warning */}
              {showUnsavedWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-amber-800 font-medium">
                    You have a role in the form that hasn't been added yet.
                  </p>
                  <div className="flex gap-3 mt-3">
                    <Button variant="secondary" size="sm" onClick={() => setShowUnsavedWarning(null)}>
                      Go Back
                    </Button>
                    <Button size="sm" onClick={() => {
                      const action = showUnsavedWarning;
                      setShowUnsavedWarning(null);
                      if (action === 'save') handleSave();
                      else handleClose();
                    }}>
                      {showUnsavedWarning === 'save' ? 'Save Without Adding' : 'Discard & Close'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={handleCloseClick}>
              Cancel
            </Button>
            <Button onClick={handleSaveClick} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserRolesModal;
